/**
 * #6a — Municipal out-of-app notifications opt-in (role-view council §6 awareness
 * gap). A minimal, first-class section of the ONE COP community panel: a not-24/7
 * municipal operator opts a phone in to jurisdiction-scoped SMS for THIS community,
 * picks per-trigger toggles, and opts out. Consent + STOP are first-class.
 *
 * Double-opt-in: opt-in creates a PENDING subscription; the worker texts a one-time
 * PIN and the subscriber replies to confirm → active. Operational-content only.
 */
import { useEffect, useState } from 'react';
import { E164_PHONE_RE, type NotificationSubscription } from '@tsr/shared-types';
import {
  createNotificationSubscription,
  fetchNotificationSubscriptions,
  patchNotificationSubscription,
} from './api';
import { showToast } from './lib/toast';

const CONSENT_LABEL: Record<NotificationSubscription['consent_state'], string> = {
  pending: 'Pending — reply to the texted code to confirm',
  opted_in: 'Active',
  opted_out: 'Opted out',
};

const CONSENT_STYLE: Record<NotificationSubscription['consent_state'], string> = {
  pending: 'bg-amber-900/40 border-amber-700 text-amber-200',
  opted_in: 'bg-emerald-900/40 border-emerald-700 text-emerald-200',
  opted_out: 'bg-slate-800 border-slate-700 text-slate-400',
};

export function NotificationSubscribeSection({
  communityId,
  communityName,
}: {
  communityId: string;
  communityName: string;
}): JSX.Element {
  const [subs, setSubs] = useState<NotificationSubscription[]>([]);
  const [phone, setPhone] = useState('');
  const [urgentEnabled, setUrgentEnabled] = useState(true);
  const [minRl, setMinRl] = useState<3 | 4>(3);
  const [assistEnabled, setAssistEnabled] = useState(true);
  const [busy, setBusy] = useState(false);

  function refresh(signal?: AbortSignal): void {
    fetchNotificationSubscriptions(communityId, signal)
      .then(setSubs)
      .catch(() => {
        /* best-effort — the panel degrades to the opt-in form */
      });
  }

  useEffect(() => {
    const ctrl = new AbortController();
    refresh(ctrl.signal);
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityId]);

  // The current LIVE subscription (pending/opted_in) for this community, if any.
  const live = subs.find((s) => s.consent_state === 'pending' || s.consent_state === 'opted_in');

  async function optIn(): Promise<void> {
    if (!E164_PHONE_RE.test(phone.trim())) {
      showToast('Enter an E.164 mobile number, e.g. +12045551234', { variant: 'error' });
      return;
    }
    setBusy(true);
    try {
      await createNotificationSubscription({
        phone: phone.trim(),
        community_id: communityId,
        urgent_alert_enabled: urgentEnabled,
        urgent_alert_min_rl: minRl,
        assist_ack_enabled: assistEnabled,
        consent_basis: `operator self-service opt-in (COP community panel — ${communityName})`,
      });
      showToast('Confirmation code texted — reply to confirm SMS alerts', { variant: 'success' });
      setPhone('');
      refresh();
    } catch {
      showToast('Could not start the subscription', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  async function optOut(id: string): Promise<void> {
    setBusy(true);
    try {
      await patchNotificationSubscription(id, { opt_out: true });
      showToast('Opted out of SMS alerts', { variant: 'success' });
      refresh();
    } catch {
      showToast('Could not opt out', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  const inputCls =
    'w-full text-xs py-1.5 px-2 rounded bg-slate-900 border border-slate-700 text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-sky-600';

  return (
    <section className="border-t border-slate-800 pt-3" data-testid="notif-subscribe-section">
      <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">
        Out-of-app notifications (SMS)
      </h3>

      {live ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-slate-300">
              {live.phone_masked}
              <span
                data-testid="notif-consent-state"
                className={`ml-2 inline-block text-[10px] py-0.5 px-1.5 rounded border ${CONSENT_STYLE[live.consent_state]}`}
              >
                {CONSENT_LABEL[live.consent_state]}
              </span>
            </span>
            <button
              type="button"
              data-testid="notif-optout"
              disabled={busy}
              onClick={() => optOut(live.id)}
              className="text-xs py-1.5 px-3 rounded bg-slate-800 hover:bg-slate-700 border border-rose-800/70 text-rose-300 font-medium disabled:opacity-50"
            >
              Opt out
            </button>
          </div>
          <p className="text-[10px] text-slate-500 leading-relaxed">
            {live.urgent_alert_enabled ? `Urgent alerts (level ≥ ${live.urgent_alert_min_rl})` : 'Urgent alerts off'}
            {' · '}
            {live.assist_ack_enabled ? 'Assist acknowledgements' : 'Assist acks off'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <input
            type="tel"
            inputMode="tel"
            data-testid="notif-phone-input"
            placeholder="+12045551234"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className={inputCls}
          />
          <label className="flex items-center gap-2 text-xs text-slate-300">
            <input
              type="checkbox"
              data-testid="notif-urgent-toggle"
              checked={urgentEnabled}
              onChange={(e) => setUrgentEnabled(e.target.checked)}
            />
            Urgent alerts in this community at level
            <select
              data-testid="notif-minrl-select"
              value={minRl}
              onChange={(e) => setMinRl(Number(e.target.value) === 4 ? 4 : 3)}
              className="text-xs py-0.5 px-1 rounded bg-slate-900 border border-slate-700 text-slate-100"
            >
              <option value={3}>≥ 3</option>
              <option value={4}>= 4</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs text-slate-300">
            <input
              type="checkbox"
              data-testid="notif-assist-toggle"
              checked={assistEnabled}
              onChange={(e) => setAssistEnabled(e.target.checked)}
            />
            Assistance-request acknowledgements
          </label>
          <button
            type="button"
            data-testid="notif-optin-submit"
            disabled={busy}
            onClick={optIn}
            className="text-xs py-1.5 px-3 rounded bg-sky-700 hover:bg-sky-600 border border-sky-600 text-white font-medium disabled:opacity-50 disabled:cursor-wait"
          >
            {busy ? 'Sending code…' : 'Notify me by SMS'}
          </button>
        </div>
      )}

      <p className="mt-2 text-[10px] text-slate-500 leading-relaxed">
        Operational alerts only — no marketing. Standard message rates may apply. Reply STOP/ARRET to
        opt out at any time. A texted confirmation code proves the number&rsquo;s owner consented.
      </p>
    </section>
  );
}
